/**
 * „Co się zmieniło między A i B" — the diverging-bars table both two-dates tabs draw: Pomiary over
 * eleven body quantities, Forma over a movement's four. Bars diverge from a centred zero on one
 * shared scale the caller's model chose, and the table is a real table to a screen reader. The
 * rows reserve `skeletonRows` rows.
 */

import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';

import skel from '~/shared/ui/skeleton.module.css';
import hidden from '~/shared/ui/visuallyHidden.module.css';

import styles from './TwoDatesTable.module.css';

export interface TwoDatesTableRow {
  readonly key: string;
  readonly label: string;
  readonly aValue: string | null;
  readonly aStale?: string | null;
  readonly bValue: string | null;
  readonly bStale?: string | null;
  readonly change: string | null;
  readonly changeIsGood: boolean;
  readonly percentLabel: string | null;
  readonly barPercent: number;
  readonly barSide: 'less' | 'more' | null;
}

interface TwoDatesTableProps {
  readonly title: string;
  /** Empty while the answer is not in: the caption describes the bars, not the wait. */
  readonly caption: string;
  readonly aHead: string;
  readonly bHead: string;
  readonly rows: readonly TwoDatesTableRow[];
  readonly skeletonRows: number;
  readonly waiting: boolean;
  readonly skeleton: boolean;
  readonly reveal: string;
  /** The failure sentence, or `null` when the read did not fail. */
  readonly failed: string | null;
  /** Said in the reserve when the answer has no row to draw. */
  readonly note?: string | null;
  /** Forma's narrower centre: short figures, B right-aligned, and a head that may wrap. */
  readonly layout?: 'compact';
}

const KEY = 'web:clientDetail.body';

export function TwoDatesTable({
  title,
  caption,
  aHead,
  bHead,
  rows,
  skeletonRows,
  waiting,
  skeleton,
  reveal,
  failed,
  note,
  layout,
}: TwoDatesTableProps) {
  const { t } = useTranslation();
  const compact = layout === 'compact' ? String(styles.compact) : '';
  return (
    <>
      <div className={`${String(styles.tableHead)} ${compact}`}>
        <h2 className={styles.tableTitle}>{title}</h2>
        <em className={styles.tableCaption}>{caption}</em>
      </div>

      {/* A real table drawn with CSS Grid, with explicit roles: `display: grid` on a `<table>`
        * drops the implicit row and cell semantics in every engine. */}
      <div aria-label={title} className={`${String(styles.dvTable)} ${compact}`} role="table">
        <div role="rowgroup">
          <div className={styles.dvHead} role="row">
            <span role="columnheader">{t(`${KEY}.columnQuantity`)}</span>
            <span className={styles.dvNumHead} role="columnheader">
              {aHead}
            </span>
            <span className={styles.dvBarHead} role="columnheader">
              {t(`${KEY}.columnBar`)}
            </span>
            <span className={styles.dvHeadB} role="columnheader">
              {bHead}
            </span>
            <span className={styles.dvNumHead} role="columnheader">
              {t(`${KEY}.columnChange`)}
            </span>
          </div>
        </div>

        <div
          aria-busy={waiting}
          className={styles.dvRows}
          role="rowgroup"
          style={{ '--dv-row-count': String(skeletonRows) } as CSSProperties}
        >
          {waiting ? (
            <>
              <span className={hidden.srOnly}>{t('web:clientDetail.states.loading')}</span>
              {skeleton
                ? Array.from({ length: skeletonRows }, (_, index) => (
                    <div className={styles.dvRow} key={index} role="row">
                      <i className={`${String(skel.bar)} ${String(styles.skelDvLabel)}`} role="cell" />
                      <i className={`${String(skel.bar)} ${String(styles.skelDvNum)}`} role="cell" />
                      <span role="cell" />
                      <i className={`${String(skel.bar)} ${String(styles.skelDvNum)}`} role="cell" />
                      <i className={`${String(skel.bar)} ${String(styles.skelDvNum)}`} role="cell" />
                    </div>
                  ))
                : null}
            </>
          ) : failed !== null || (rows.length === 0 && note != null) ? (
            <div className={`${String(styles.dvNotice)} ${reveal}`} role="row">
              <span role="cell">{failed ?? note}</span>
            </div>
          ) : (
            rows.map((row) => (
              <div className={`${String(styles.dvRow)} ${reveal}`} key={row.key} role="row">
                <span className={styles.dvLabel} role="rowheader">
                  {row.label}
                </span>
                <span className={styles.dvNum} role="cell">
                  {row.aValue ?? '·'}
                  {row.aStale == null ? null : <small className={styles.dvStale}>{row.aStale}</small>}
                </span>
                <span className={styles.dvBar} role="cell">
                  {row.barSide === null ? null : (
                    <>
                      <i
                        className={`${String(styles.dvFill)} ${row.changeIsGood ? String(styles.dvFillGood) : ''}`}
                        style={
                          row.barSide === 'less'
                            ? { insetInlineEnd: '50%', inlineSize: `${String(row.barPercent)}%` }
                            : { insetInlineStart: '50%', inlineSize: `${String(row.barPercent)}%` }
                        }
                      />
                      <b
                        className={styles.dvPercent}
                        style={
                          row.barSide === 'less'
                            ? {
                                insetInlineEnd: `calc(50% + ${String(row.barPercent)}% + var(--dv-percent-gap))`,
                              }
                            : {
                                insetInlineStart: `calc(50% + ${String(row.barPercent)}% + var(--dv-percent-gap))`,
                              }
                        }
                      >
                        {row.percentLabel}
                      </b>
                    </>
                  )}
                </span>
                <span className={`${String(styles.dvNum)} ${String(styles.dvNumB)}`} role="cell">
                  {row.bValue ?? '·'}
                  {row.bStale == null ? null : <small className={styles.dvStale}>{row.bStale}</small>}
                </span>
                <span
                  className={`${String(styles.dvChange)} ${row.changeIsGood ? String(styles.dvChangeGood) : ''}`}
                  role="cell"
                >
                  {row.change ?? <em className={styles.dvNoPair}>{t(`${KEY}.noPairShort`)}</em>}
                </span>
              </div>
            ))
          )}
        </div>
      </div>
    </>
  );
}
